/**
 * ColumnPlacement — the placement decision for a column whose FULL bind is
 * being held by the motion hold. Pure, so the adapter's one shortcut that can
 * strand a column at stale geometry is pinned in a DOM-free test.
 *
 * Placement is geometry only: index, offset and width. It never touches the
 * markers that say whether a column is BOUND (`recycled`, `pending-layout`,
 * the bound-collection `cid`) — those are the full bind's to clear — so the
 * plan carries no such keys by construction. A column already at every
 * desired value with live geometry is left alone; any difference, at the same
 * index or another, is applied, because the held FULL debt (sticky) means no
 * resize bind will rescue a same-index geometry change.
 */
export namespace ColumnPlacement {
  export interface Current {
    readonly idx: string;
    readonly offset: string;
    readonly width: string;
    readonly pooled: boolean;
  }
  export interface Desired {
    readonly index: number;
    readonly offsetPx: number;
    readonly widthPx: number;
  }
  export type Styles = { readonly "--idx": string; readonly "--offset": string; readonly width: string };
  export type Plan =
    | { readonly kind: "skip" }
    | { readonly kind: "apply"; readonly styles: Styles; readonly unpool: boolean };

  export function styles(desired: Desired): Styles {
    return { "--idx": String(desired.index), "--offset": desired.offsetPx + "px", width: desired.widthPx + "px" };
  }

  /**
   * Does the column carry another index's content? A placed column that was
   * live at some other index keeps that content until its full bind lands at
   * release — a week of November sitting between two weeks of March, for the
   * whole gesture (observed on the frame strip, project sheet-scroll-
   * smoothness, 2026-09-12). The adapter hides such content on place; a
   * column placed back at the index it is bound to shows it again, and a
   * pooled column is stale wherever it is placed: it has no
   * bound index, yet it still carries what it showed before it parked (column
   * retention keeps it) and its park mark; placed ahead of its bind, at a
   * re-anchor's installation, it must hide that content and paint its
   * skeleton rather than stay blank.
   */
  export function stale(boundIdx: Option<string>, desired: Desired, pooled: boolean, recycled: boolean): boolean {
    return pooled || recycled || (boundIdx != null && boundIdx !== String(desired.index));
  }
  export function plan(current: Current, desired: Desired): Plan {
    const want = styles(desired);
    const unchanged = !current.pooled &&
      current.idx === want["--idx"] &&
      current.offset === want["--offset"] &&
      current.width === want.width;
    if (unchanged) return { kind: "skip" };
    return { kind: "apply", styles: want, unpool: current.pooled };
  }
}
